import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { Detail, Checkbox, Tooltip } from 'components/common';
import { TreeSelect } from 'components/common';
import WorkItemTree from './WorkItemTree';
import styled from 'styled-components';
import { debounce } from 'lodash';

// Add styled components
const ActivitySelectorContainer = styled.div`
  width: 100%;
`;

const ActivityCount = styled.div`
  text-align: right;
  color: #8d8d8d;
  font-size: 13px;
  margin-top: 3px;
`;

function ActivitySelector({
  allItems,
  recentItems,
  searchResults,
  searchLoading,
  hasActivities,
  allItemsLoading,
  recentItemsLoading,
  workItemId,
  excludeOldWorkItems,
  onExcludeOldWorkItemsChange,
  loading,
  disabled,
  onSelectionChange,
  clearSelectionTrigger,
  handleActivitySearch,
}) {
  // Selection state
  const [selectedWorkItems, setSelectedWorkItems] = useState([]);
  const [selectedWorkItemLabels, setSelectedWorkItemLabels] = useState({});
  const [initialWorkItem, setInitialWorkItem] = useState([]);
  const [initialWorkItemProcessed, setInitialWorkItemProcessed] = useState(
    false
  );

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Create debounced version of handleActivitySearch
  const debouncedActivitySearch = debounce(term => {
    if (handleActivitySearch) {
      handleActivitySearch(term);
    }
  }, 500);

  // Reset search when dropdown loses focus
  const resetSearch = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    setSearchTerm('');
    setIsSearching(false);
    // Also clear the parent's search if we're resetting
    if (handleActivitySearch) {
      handleActivitySearch('');
    }
  }, [handleActivitySearch]);

  // Effect to update searching state based on search term
  useEffect(() => {
    if (!searchTerm || searchTerm.trim() === '') {
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
  }, [searchTerm]);

  // Handle selection change
  const handleSelectedWorkItemsChange = useCallback(
    (value, node) => {
      setSelectedWorkItems(value);
      setSelectedWorkItemLabels(prev => {
        const updatedLabels = {};
        value.forEach((val, index) => {
          const category = node[index].category;
          if (!updatedLabels[category]) {
            updatedLabels[category] = {};
          }
          updatedLabels[category][val] = node[index];
        });
        Object.keys(prev).forEach(category => {
          if (!updatedLabels[category]) {
            delete updatedLabels[category];
          } else {
            Object.keys(prev[category]).forEach(key => {
              if (!value.includes(key)) {
                delete updatedLabels[category][key];
              }
            });
          }
        });
        return updatedLabels;
      });
      if (initialWorkItem.length) setInitialWorkItem([]);
    },
    [initialWorkItem.length]
  );

  // Expose selection to parent
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedWorkItems, selectedWorkItemLabels);
    }
  }, [selectedWorkItems, selectedWorkItemLabels, onSelectionChange]);

  // Tree data logic
  const getDisplayWorkItems = useCallback(() => {
    if (isSearching && searchResults) {
      return searchResults;
    }
    return excludeOldWorkItems ? recentItems : allItems;
  }, [isSearching, searchResults, excludeOldWorkItems, recentItems, allItems]);

  const selectedWorkItemsTreeData = useMemo(() => {
    const currentDisplayWorkItems = getDisplayWorkItems();
    if (!currentDisplayWorkItems) return [];
    const shouldProcessWorkItemId =
      workItemId &&
      !initialWorkItemProcessed &&
      currentDisplayWorkItems?.groupedResults?.length > 0;
    return WorkItemTree.buildWorkItemTreeData(
      currentDisplayWorkItems,
      selectedWorkItemLabels,
      shouldProcessWorkItemId ? setInitialWorkItem : () => {},
      shouldProcessWorkItemId ? workItemId : null
    );
  }, [
    getDisplayWorkItems,
    selectedWorkItemLabels,
    workItemId,
    initialWorkItemProcessed,
  ]);

  // Process initial work item selection if provided
  useEffect(() => {
    if (initialWorkItem.length && !initialWorkItemProcessed) {
      setInitialWorkItemProcessed(true);
      handleSelectedWorkItemsChange(initialWorkItem[0], initialWorkItem[1]);
    }
  }, [
    initialWorkItem,
    initialWorkItemProcessed,
    handleSelectedWorkItemsChange,
  ]);

  // Reset the processed flag when workItemId changes
  useEffect(() => {
    setInitialWorkItemProcessed(false);
  }, [workItemId]);

  // Loading state for the selector
  const isSelectorLoading = loading || allItemsLoading || recentItemsLoading;

  // Effect to clear selection when clearSelectionTrigger changes
  useEffect(() => {
    setSelectedWorkItems([]);
    setSelectedWorkItemLabels({});
  }, [clearSelectionTrigger]);

  // Handle search input change and update parent's search term
  const handleSearchChange = useCallback(
    term => {
      setSearchTerm(term);
      debouncedActivitySearch(term);
    },
    [debouncedActivitySearch]
  );

  return (
    <>
      <Detail label={<>Activities</>}>
        <Tooltip
          title={
            !hasActivities
              ? 'Option disabled because the contact has no activities.'
              : ''
          }
        >
          <ActivitySelectorContainer>
            <TreeSelect
              getPopupContainer={triggerNode => triggerNode.parentNode}
              selectedValues={selectedWorkItems}
              size='middle'
              filterTreeNode={false}
              treeCheckable
              showArrow
              maxCount={3}
              placeholder={'Type to search activities (e.g. carcinoma)'}
              treeData={selectedWorkItemsTreeData}
              onChange={handleSelectedWorkItemsChange}
              style={{ width: '100%' }}
              onSearch={handleSearchChange}
              onDropdownBlur={resetSearch}
              loading={searchLoading || isSelectorLoading}
              autoClearSearchValue={true}
              treeExpandAction={'click'}
              disabled={isSelectorLoading || disabled || !hasActivities}
            />
            <ActivityCount>{selectedWorkItems.length} / 3</ActivityCount>
          </ActivitySelectorContainer>
        </Tooltip>
      </Detail>
      <Detail style={{ marginBottom: '16px' }}>
        <Tooltip
          title={
            !hasActivities
              ? 'Option disabled because the contact has no activities.'
              : 'When enabled, activities older than one year will be deprioritised in favor of more recent and future activities. For example, if an activity within the past year has 5 keyword matches and an activity from 2 years ago has 30 matches, the more recent activity would be prioritised.'
          }
        >
          <Checkbox
            checked={excludeOldWorkItems}
            disabled={isSelectorLoading || disabled || !hasActivities}
            onChange={onExcludeOldWorkItemsChange}
            labelText='Deprioritise activities older than one year'
          ></Checkbox>
        </Tooltip>
      </Detail>
    </>
  );
}

ActivitySelector.propTypes = {
  allItems: PropTypes.object,
  recentItems: PropTypes.object,
  searchResults: PropTypes.object,
  searchLoading: PropTypes.bool,
  hasActivities: PropTypes.bool,
  allItemsLoading: PropTypes.bool,
  recentItemsLoading: PropTypes.bool,
  workItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  excludeOldWorkItems: PropTypes.bool,
  onExcludeOldWorkItemsChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  clearSelectionTrigger: PropTypes.number,
  handleActivitySearch: PropTypes.func,
};

ActivitySelector.defaultProps = {
  excludeOldWorkItems: true,
  hasActivities: false,
  searchLoading: false,
  loading: false,
  disabled: false,
  allItemsLoading: false,
  recentItemsLoading: false,
};

export default ActivitySelector;
